import { add, isBefore, parseISO, startOfDay, startOfHour, startOfMinute, startOfMonth, startOfSecond, startOfYear } from 'date-fns';
import { toDate } from 'date-fns-tz';
import h from 'hyperscript';
import pick from 'lodash-es/pick';
import isEqual from 'lodash-es/isEqual';
import leftIcon from '@disy/cadenza-icons/chevron-left.svg';
import rightIcon from '@disy/cadenza-icons/chevron-right.svg';
import settingsConsole from '@disy/cadenza-icons/settings-console.svg';

import { createSlider } from 'ui/slider/slider';
import { PLACEMENT, setupPopupButton } from 'ui/popup/popup';
import { createIconButton } from 'ui/button/button';

import { formatDateWithJavaFormat } from 'cadenza/utils/date-time/date-format';
import { DatePicker } from 'cadenza/conditions/date-picker/date-picker';
import { on, unByKey } from 'cadenza/utils/event-util';
import { CHANGED, CONDITIONS_HIERARCHIES_CHANGE, FILTERS_APPLIED } from 'cadenza/conditions/event-types';
import { icon } from 'cadenza/utils/icon/icon';
import { logger } from 'cadenza/utils/logging';
import { ConditionApi } from 'cadenza/api-client/conditions/condition-api';
import { DateConditionMode } from 'cadenza/conditions/date-time-condition/date-condition-mode';
import { CONDITION_SETTINGS } from 'cadenza/workbook/condition-edit-dialog/condition-settings';
import { DateConditionFilterMode } from 'cadenza/conditions/date-time-condition/date-condition-filter-mode';
import {
  COMPONENT_NAME as ADHOC_SETTINGS_PANEL_COMPONENT_NAME,
  DATE_RANGE_CHANGE,
  DateTimeConditionAdHocSettingsPanel,
  SETTINGS_CHANGE
} from 'cadenza/conditions/date-time-condition/date-time-condition-ad-hoc-settings-panel';
import { ConditionElement } from 'cadenza/conditions/condition-element';
import { FeatureName, isFeatureAvailable } from 'cadenza/features';
import {
  adaptConditionValueToStepsConfig,
  adaptStepsConfigToConditionValue,
  calculateSteppedSliderRange,
  canStepFromRelativeDate,
  dateConditionRangeValueEquals,
  differenceInTickUnit,
  mostFineGrainedDateFormatTickUnit,
  mostFineGrainedTickUnit, roundToTickUnit, StepDirection,
  stepsConfigEquals,
  stepsConfigFromRelativeDate,
  TickUnit,
  TickUnitOrder,
  toConditionSettings,
  toStepsConfig
} from 'cadenza/conditions/date-time-condition/date-time-condition-utils';
import {
  DateAnimationSettingsDialog
} from 'cadenza/conditions/date-time-condition/date-animation-settings-dialog';
import { getAttributeById, getConditionById, getWorkbookIdAndHash } from 'cadenza/workbook/workbook.state';
import { store } from 'cadenza/store/cadenza-store';
import { getWorkbookConditionContextSensitiveData } from 'cadenza/api-client/workbook-api';

import './date-time-condition.css';
import i18n from './messages.properties';

const COMPONENT_NAME = 'd-date-time-condition';

/**
 * @typedef StepsConfig
 * @property {TickUnit} unit - the unit in which the step time window and overlap is specified
 * @property {int} timeWindow - the amount of time displayed at once for each step (number to be
 *   interpreted according to the "unit" property)
 * @property {int} overlap - the amount of time overlap between two subsequent steps (number to be
 *   interpreted according to the "unit" property)
 */

const DEFAULT_STEP_DELTA = 1;

export class DateTimeCondition extends ConditionElement {

  #isInterval;
  #dateFormat;
  #timeZoneId;
  #separator;
  #conditionApi;
  #tickUnit;
  #staticDatePickerConfiguration;
  #datePickerDiv;
  #datePicker;
  #prevButton;
  #nextButton;
  #sliderDiv;
  #sliderStartDateTime;
  _sliderCustomStartDateTime;
  #sliderLastStartDateTime;
  #sliderEndDateTime;
  _sliderCustomEndDateTime;
  #sliderLastEndDateTime;
  _sliderStartLabel;
  _sliderEndLabel;
  #lastRow;
  #eventKeys = [];
  _stepsSliderEventKeys = [];
  #animateButton;
  _animateButtonTooltip;
  #tickCount;
  #resolvedDate;
  #resolvedDateValue;
  #transientDatePickerEventKey;
  #conditionsApplied = true;
  #latestSetValuesPromise; // for keeping consistency for other conditions, see onConditionChange

  _lastAppliedTickValues;
  _dateConfiguration;
  _values;
  _title;
  _conditionApiSupplier;
  _timeSliderApiSupplier;
  // Whether the component is in 'Slider' or 'Calender' mode.
  _dateConditionMode;
  /** @type {StepsConfig} */
  _stepsConfig;
  _stepDelta = 1;
  _maxLowerTickValue;
  _affectedMapViews;
  _activeFilterMode;

  constructor ({
    dateConfiguration,
    values,
    conditionApiSupplier = configObject => new ConditionApi(configObject),
    timeSliderApiSupplier = (layerId, conditionId) => import(/* webpackChunkName: "classic-time-slider-api" */'cadenza/api-client/timeslider/classic-time-slider-api')
      .then(({ ClassicTimeSliderApi }) => new ClassicTimeSliderApi(layerId, conditionId)),
    title,
    settings, affectedMapViews = []
  } = {}) {
    super();
    this._dateConfiguration = dateConfiguration;
    this._values = values?.valueItems[0];
    this._title = title;
    this._conditionApiSupplier = conditionApiSupplier;
    this._timeSliderApiSupplier = timeSliderApiSupplier;
    this._settings = settings;
    this._affectedMapViews = affectedMapViews;
    this._isInitialized = false;
    // Workbooks: when both custom start and end date are set, then the condition is not context sensitive anymore.
    if (this.isWorkbookCondition && this._hasCustomStartAndCustomEndDate()) {
      this._settings.contextSensitive = false;
    }
  }

  _createStepsConfig () {
    const isWorkbookCondition = this.isWorkbookCondition;
    const unit = isWorkbookCondition
      ? this._settings[CONDITION_SETTINGS.DATE_CONDITION_INTERVAL_UNIT]
      : this._getDateConfiguration().sliderConfiguration?.intervalUnit?.toLowerCase();
    let config;
    // if one setting is available, then all should be available
    if (unit) {
      const timeWindow = isWorkbookCondition
        ? this._settings[CONDITION_SETTINGS.DATE_CONDITION_INTERVAL_LENGTH]
        : this._getDateConfiguration().sliderConfiguration?.intervalLength;
      const overlap = isWorkbookCondition
        ? this._settings[CONDITION_SETTINGS.DATE_CONDITION_INTERVAL_OVERLAP_LENGTH]
        : this._getDateConfiguration().sliderConfiguration?.overlapCount;
      config = {
        unit,
        timeWindow,
        overlap
      };
    } else {
      const timeWindow = this._createStepsTimeWindowFromValue();
      config = {
        unit: mostFineGrainedTickUnit(this._getDateConfiguration()),
        timeWindow,
        overlap: Math.max(timeWindow - 1, 0)
      };
    }
    config = this._adaptRelativeStepsConfigForClassic(config);
    return config;
  }

  _adaptRelativeStepsConfigForClassic (config) {
    // Adapt steps settings for relative date and persist in session store for classic web.
    const data = this.previousElementSibling?.value;
    if (data?.value.relativeDate) { // data is not available in workbooks.
      return stepsConfigFromRelativeDate(data.value.relativeDate);
    }
    return config;
  }

  /**
   * Adapts the step config to fit the condition value if necessary, i.e. if the condition has a
   * value range.
   *
   * @param {StepsConfig} stepsConfig - steps config to be adapted; is left unchanged
   * @param {[string, string]} conditionValue - start and end date as ISO date strings
   * @return {StepsConfig} - the new steps config
   * @private
   */
  _adaptStepsConfigToConditionValueIfNecessary (stepsConfig, conditionValue) {
    const valueSetAndIsRange = conditionValue && conditionValue[0] && conditionValue[1];
    if (valueSetAndIsRange) {
      return adaptStepsConfigToConditionValue(
        new Date(conditionValue[0]),
        new Date(conditionValue[1]),
        stepsConfig,
        mostFineGrainedTickUnit(this._getDateConfiguration())
      );
    }
    return stepsConfig;
  }

  _createStepsTimeWindowFromValue () {
    const start = this.value?.start;
    const end = this.value?.end;

    if (!start || !end) {
      // fixed default if not value is set or the value is open-ended at one end
      return 1;
    }

    return differenceInTickUnit(
      toDate(start),
      toDate(end),
      mostFineGrainedTickUnit(this._getDateConfiguration())) + 1;
  }

  connectedCallback () {
    super.connectedCallback();
    this.classList.add(COMPONENT_NAME);
    const persistedFilterMode = this._getDateConfiguration().sliderConfiguration?.activeFilterMode
      ?? DateConditionFilterMode.DATA_INPUT;
    const persistedSliderMode = this._getDateConfiguration().sliderConfiguration?.mode
      ?? this.getAttribute('date-time-mode');
    this._dateConditionMode = this._settings
      ? this._settings[CONDITION_SETTINGS.DATE_CONDITION_MODE]
      : persistedSliderMode;

    this._activeFilterMode = this._settings
      ? this._settings[CONDITION_SETTINGS.DATE_CONDITION_ACTIVE_FILTER_MODE]
      : persistedFilterMode;

    this.#conditionApi = this._conditionApiSupplier({
      conditionSetId: this.conditionSetId,
      conditionName: this.conditionName,
      conditionId: this.conditionId,
      conditionValuesSetHashGetter: () => this.conditionValuesSetHash
    });

    if (this._dateConditionMode === DateConditionMode.SLIDER) {
      this._setupSliderMode();
    } else {
      this._setSliderValues(this._getSliderConfig());
      this._setupCalendarMode();
    }

    // In Cadenza Classic in slider mode _stepsConfig is initialized before, in initSlider
    if (!this._stepsConfig) {
      this._stepsConfig = this._createStepsConfig();
    }

    // In classic the button to open adhoc-settings-panel is shown only for SLIDER mode.
    if (!this.isWorkbookCondition && this.getAttribute('date-time-mode') === DateConditionMode.SLIDER) {
      this._replaceHeaderButtonPlaceholder();
    }
  }

  /**
   * In case the attribute date format changed, it might be necessary to adjust the current
   * condition settings to that new format.
   * Should only be called on workbook condition (not in a Cadenza Classic context).
   *
   * @return {object|undefined} - The updated condition settings.
   */
  adjustConditionSettings () {
    let newStepsConfig;
    let newSettings;
    const newDateFormat = this._getAdjustedDateFormat();
    if (newDateFormat) {
      newStepsConfig = this._getAdjustedStepsConfiguration(newDateFormat);
      newSettings = {
        [CONDITION_SETTINGS.DATE_CONDITION_FORMAT]: newDateFormat
      };
      if (newStepsConfig) {
        newSettings[CONDITION_SETTINGS.DATE_CONDITION_INTERVAL_LENGTH] = newStepsConfig.timeWindow;
        newSettings[CONDITION_SETTINGS.DATE_CONDITION_INTERVAL_UNIT] = newStepsConfig.unit;
        newSettings[CONDITION_SETTINGS.DATE_CONDITION_INTERVAL_OVERLAP_LENGTH] = newStepsConfig.overlap;
      }
    }
    if (this._values?.relativeDate) { // A newly created condition might not have values
      const stepsConfigForRelativeDate = stepsConfigFromRelativeDate(this._values.relativeDate);
      const currentStepsConfig = newStepsConfig || toStepsConfig(this._settings || {});
      if (!stepsConfigEquals(stepsConfigForRelativeDate, currentStepsConfig)) {
        newSettings = newSettings || {};
        Object.assign(newSettings, toConditionSettings(stepsConfigForRelativeDate));
      }
    }

    return newSettings;
  }

  /**
   * Update condition settings if the attribute settings have changed. (Workbook specific)
   *
   * @param {object} newSettings - The new condition settings.
   */
  updateConditionSettings (newSettings) {
    const api = this._conditionApiSupplier();
    setTimeout(() => { api.setSettings(newSettings); });
  }

  // In Workbooks the condition can have a date format setting that might need to be changed if the
  // attribute date format has changed to a larger granularity than the condition date format.
  _getAdjustedDateFormat () {
    if (!this._settings[CONDITION_SETTINGS.DATE_CONDITION_FORMAT]) { // "auto" mode, attribute date format is used.
      return;
    }

    const condition = getConditionById(store.getState(), this._conditionApiSupplier().conditionId);
    const attribute = getAttributeById(store.getState(), condition.attributeIds[0]);
    const smallestAttributeDateUnit = mostFineGrainedDateFormatTickUnit(attribute.format);

    const smallestConditionDateUnit = mostFineGrainedDateFormatTickUnit(this._settings[CONDITION_SETTINGS.DATE_CONDITION_FORMAT]);

    let newDateFormat;
    if (TickUnitOrder[smallestConditionDateUnit] < TickUnitOrder[smallestAttributeDateUnit]) {
      newDateFormat = attribute.format;
    }
    return newDateFormat;
  }

  _getAdjustedStepsConfiguration (dateFormat) {
    const smallestDateUnit = mostFineGrainedDateFormatTickUnit(dateFormat);
    const stepsUnit = this._settings[CONDITION_SETTINGS.DATE_CONDITION_INTERVAL_UNIT];
    let newStepsConfig;
    if (stepsUnit && TickUnitOrder[stepsUnit] < TickUnitOrder[smallestDateUnit]) {
      newStepsConfig = {
        unit: smallestDateUnit,
        timeWindow: 1,
        overlap: 0
      };
    }
    return newStepsConfig;
  }

  getHeaderButtons () {
    const filterModeOptionsSupplier = () => {
      const enabledFilterModes = [];
      const dataInputEnabled = this._settings[CONDITION_SETTINGS.DATE_CONDITION_MODE_DATA_INPUT_ENABLED];
      const stepsEnabled = this._settings[CONDITION_SETTINGS.DATE_CONDITION_MODE_STEPS_ENABLED];
      const conditionModeUnset = dataInputEnabled === undefined && stepsEnabled === undefined;
      if (dataInputEnabled || conditionModeUnset) {
        enabledFilterModes.push(DateConditionFilterMode.DATA_INPUT);
      }
      if (stepsEnabled || conditionModeUnset) {
        enabledFilterModes.push(DateConditionFilterMode.STEPS);
      }

      const dateConfiguration = { ...this._getDateConfiguration() };

      if (this._settings[CONDITION_SETTINGS.DATE_CONDITION_FORMAT]) {
        const conditionTickUnit =
          mostFineGrainedDateFormatTickUnit(this._settings[CONDITION_SETTINGS.DATE_CONDITION_FORMAT]);
        dateConfiguration.secondEnabled = TickUnitOrder[conditionTickUnit] <= TickUnitOrder[TickUnit.SECONDS];
        dateConfiguration.minuteEnabled = TickUnitOrder[conditionTickUnit] <= TickUnitOrder[TickUnit.MINUTES];
        dateConfiguration.hourEnabled = TickUnitOrder[conditionTickUnit] <= TickUnitOrder[TickUnit.HOURS];
        dateConfiguration.dayEnabled = TickUnitOrder[conditionTickUnit] <= TickUnitOrder[TickUnit.DAYS];
        dateConfiguration.monthEnabled = TickUnitOrder[conditionTickUnit] <= TickUnitOrder[TickUnit.MONTHS];
        dateConfiguration.yearEnabled = TickUnitOrder[conditionTickUnit] <= TickUnitOrder[TickUnit.YEARS];
      }

      return {
        initialFilterMode: this._settings[CONDITION_SETTINGS.DATE_CONDITION_ACTIVE_FILTER_MODE]
          ?? DateConditionFilterMode.DATA_INPUT,
        initialSliderMode: this._settings[CONDITION_SETTINGS.DATE_CONDITION_MODE]
          ?? DateConditionMode.SLIDER,
        initialStepsConfig: this._stepsConfig,
        enabledFilterModes,
        dateConfiguration,
        sliderValues: this._getSliderValues(),
        conditionValue: this.value,
        isInterval: this.#isInterval,
        isRelativeDate: Boolean(this.value?.relativeDate),
        contextValueFn: this._getContextSensitiveValues.bind(this)
      };
    };

    return [ this._createDateConditionFilterModeButton(filterModeOptionsSupplier) ];
  }

  // Handles only classic web
  _replaceHeaderButtonPlaceholder () {
    const filterModeOptionsSupplier = () => {
      return {
        initialFilterMode: this._activeFilterMode,
        initialSliderMode: this._dateConditionMode,
        initialStepsConfig: this._stepsConfig,
        enabledFilterModes: [ DateConditionFilterMode.DATA_INPUT, DateConditionFilterMode.STEPS ],
        dateConfiguration: this._getDateConfiguration(),
        sliderValues: this._getSliderValues(),
        conditionValue: this.value,
        isInterval: this.#isInterval,
        isRelativeDate: Boolean(this.value?.relativeDate)
      };
    };

    const headerButton = this._createDateConditionFilterModeButton(filterModeOptionsSupplier);
    on(headerButton, SETTINGS_CHANGE, event => {
      const changedSettings = event.detail.value;
      if (changedSettings[CONDITION_SETTINGS.DATE_CONDITION_MODE]) {
        this._saveModeSettings(changedSettings);
        this.#sliderDiv.hidden =
          changedSettings[CONDITION_SETTINGS.DATE_CONDITION_MODE] === 'Calendar';
        this._toggleStepsButtons();
      }
      // if DATE_CONDITION_INTERVAL_UNIT is changed, then all step settings are changed
      if (changedSettings[CONDITION_SETTINGS.DATE_CONDITION_INTERVAL_UNIT]) {
        const config = this._adaptRelativeStepsConfigForClassic(toStepsConfig(changedSettings));
        this._onStepsConfigChanged(config);
      }
      if (this._isSteppingMode) { // Show animate button only if in stepsmode.
        if (!this.#animateButton) {
          const { sliderConfiguration } = this._getDateConfiguration();
          const animationAllowed = sliderConfiguration.sliderAnimationAllowed;
          this._initAnimationButton(animationAllowed)?.then(() => {
            this.#animateButton.hidden = false;
          });
        } else {
          this.#animateButton.hidden = false;
        }
      } else if (this.#animateButton) {
        this.#animateButton.hidden = true;
      }
      this._updateDynamicConfiguration({});
    });
    const buttonPlaceholder =
      this.closest('.d-condition-wrapper')
        .querySelector('.d-condition--header .action-button-placeholder');
    buttonPlaceholder.replaceWith(h('.d-condition--spacer'), headerButton);
  }

  _saveModeSettings (workbookSettings) {
    this._dateConditionMode = workbookSettings[CONDITION_SETTINGS.DATE_CONDITION_MODE];
    this._activeFilterMode = workbookSettings[CONDITION_SETTINGS.DATE_CONDITION_ACTIVE_FILTER_MODE];

    this.#conditionApi.setValue(this.conditionValue, this._getClassicCustomProperties())
      .then((json) => super.onConditionChange(json))
      .catch(error => this.handleConditionValueChangeError(error));
  }

  _getClassicCustomProperties () {
    const customProperties = pick(this._getSliderValues(), [ 'sliderCustomStartDate', 'sliderCustomEndDate' ]);
    customProperties.intervalUnit = tickUnitToJavaChronoUnit(this._stepsConfig.unit);
    customProperties.overlapCount = this._stepsConfig.overlap.toString();
    customProperties.intervalLength = this._stepsConfig.timeWindow.toString();
    customProperties.activeFilterMode = this._activeFilterMode;
    customProperties.mode = this._dateConditionMode;

    return customProperties;
  }

  _getDateConfiguration () {
    const data = this.previousElementSibling?.value;
    return data?.dateConfiguration ?? this._dateConfiguration;
  }

  _canPlayAnimation (animationAllowed) {
    const data = this.previousElementSibling?.value;
    return (data?.canPlayMapAnimation ?? isFeatureAvailable(FeatureName.PLAY_MAP_ANIMATION)) // has System Privilege
      && (this._isSteppingMode || !this.#isInterval) // should be steps mode or point in time mode
      && animationAllowed // animation is allowed for this condition
      && this.isMapDisplayed(this._affectedMapViews); // there is at least 1 map view affected by this condition
  }

  _setupCalendarMode () {
    this.classList.add('calendar-mode');
    const data = this.previousElementSibling?.value;
    const dateConfiguration = data?.dateConfiguration ?? this._dateConfiguration;
    this.#isInterval = dateConfiguration.isInterval ? dateConfiguration.isInterval : false;
    this.#datePicker = new DatePicker({
      title: this.conditionLabel ?? this._title,
      ...dateConfiguration
    });
    this.#datePicker.enabled = this.enabled;
    this.#resolvedDateValue = h('span.d-date-time-condition--relative-date-resolved-value');
    this.#resolvedDate = h('.d-date-time-condition--relative-date-resolved', { hidden: true }, [
      this.#resolvedDateValue
    ]);
    const defaultValue = this.value;
    this.value = data?.value ?? this._values;
    this.defaultValue = data?.defaultValue ?? defaultValue;
    this.#datePicker.addEventListener('date-selected', () => this.onConditionChange());
    this.append(this.#datePicker, this.#resolvedDate);
    this.initChanged();
  }

  _setupSliderMode () {
    const data = this.previousElementSibling?.value;
    const dateConfiguration = data?.dateConfiguration ?? this._dateConfiguration;
    const {
      secondEnabled,
      minuteEnabled,
      hourEnabled,
      dayEnabled,
      monthEnabled,
      yearEnabled,
      sliderConfiguration
    } = dateConfiguration;
    this.#isInterval = dateConfiguration.isInterval ? dateConfiguration.isInterval : false;
    this.#separator = dateConfiguration.separator;
    this.#dateFormat = this._settings?.[CONDITION_SETTINGS.DATE_CONDITION_FORMAT] || dateConfiguration.dateFormat;
    this.#timeZoneId = dateConfiguration.timeZoneId;
    const dateSuggestions = dateConfiguration.relativeDateSuggestions;

    const {
      tickUnit,
      tickCount,
      sliderStartDate,
      sliderEndDate,
      sliderLastStartDate,
      sliderLastEndDate,
      sliderAnimationAllowed,
      sliderCustomStartDate,
      sliderCustomEndDate
    } = this._getSliderConfig(sliderConfiguration);

    this.#staticDatePickerConfiguration = {
      isInterval: this.#isInterval,
      dateFormat: this.#dateFormat,
      separator: this.#separator,
      yearEnabled,
      monthEnabled,
      dayEnabled,
      hourEnabled,
      minuteEnabled,
      secondEnabled,
      relativeDateSuggestions: dateSuggestions
    };
    const dateConditionUnit = this._settings
      ? mostFineGrainedDateFormatTickUnit(this._settings[CONDITION_SETTINGS.DATE_CONDITION_FORMAT])
      : null;
    const normalizedTickUnit = dateConditionUnit || tickUnit.toLowerCase().trim();
    if (!Object.values(TickUnit).includes(normalizedTickUnit)) {
      throw new Error(`Tick unit '${normalizedTickUnit}' is not supported.`);
    }
    this.#tickUnit = normalizedTickUnit;

    this.#prevButton = h('button.button.button-icon.button-borderless', {
      'type': 'button',
      'aria-pressed': 'false',
      'title': i18n('prev'),
      'onclick': () => {
        this._step(StepDirection.PREVIOUS);
      }
    }, [ icon(leftIcon) ]);
    this.#nextButton = h('button.button.button-icon.button-borderless', {
      'type': 'button',
      'aria-pressed': 'false',
      'title': i18n('next'),
      'onclick': () => {
        this._step(StepDirection.NEXT);
      }
    }, [ icon(rightIcon) ]);

    this._sliderStartLabel = h('label.slider-start-date.ellipsis');
    this._sliderEndLabel = h('label.slider-end-date.ellipsis');

    this.#lastRow = h('.condition-container.last-row-container', [
      this._sliderStartLabel,
      this._sliderEndLabel
    ]);

    this.#resolvedDateValue = h('span.d-date-time-condition--relative-date-resolved-value');
    this.#resolvedDate = h('.d-date-time-condition--relative-date-resolved', { hidden: true }, [
      this.#resolvedDateValue
    ]);

    this.#datePickerDiv = h('.d-date-time-condition--date-picker-row', this.#datePicker);
    this.append(this.#datePickerDiv);
    this.#sliderDiv = h('.d-date-time-condition-slider');
    try {
      this.append(h('.d-date-time-condition-box', this.#sliderDiv), this.#resolvedDate);
    } catch (error) {
      logger.error(`Could not initialize correctly time slider condition: ${error}`);
    }

    const config = {
      tickCount,
      sliderStartDate,
      sliderEndDate,
      sliderCustomStartDate,
      sliderCustomEndDate,
      sliderLastStartDate,
      sliderLastEndDate
    };

    const filterController = this.filterController;
    if (filterController) {
      // Classic
      this.#eventKeys.push(on(filterController, CHANGED, () => {
        // This event will be raised when the changes of filters are applied so
        // the component can animate the result
        this._setStateToConditionsAlreadyApplied();
      }));
      if (sliderConfiguration.isCalculatedAutomatically) {
        this.#eventKeys.push(on(
          filterController,
          CONDITIONS_HIERARCHIES_CHANGE,
          event => this._filterCriteriaChanged(event)));
      }
      this._updateDynamicConfiguration(config);
    } else {
      this._initDatePicker(config);
    }

    this._initAnimationButton(sliderAnimationAllowed);

    const defaultValue = this.value;
    this.value = data?.value ?? this._values;
    this.defaultValue = data?.defaultValue ?? defaultValue;
    this.initChanged();

    // Workbooks
    // When both custom start and end date are set, then the condition is not context sensitive anymore,
    // so the total slider date range is set to these values.
    if (this._settings && this._settings.contextSensitive === false) {
      config.sliderStartDate = this._settings.sliderCustomStartDate;
      config.sliderEndDate = this._settings.sliderCustomEndDate;
      this._updateDynamicConfiguration(config);
    }
    // set timeout to make sure, that elements are in the dom and visible
    setTimeout(() => {
      this._updateUi();
    });
  }

  _getSliderConfig (sliderConfiguration) {
    sliderConfiguration = sliderConfiguration ?? this._getDateConfiguration().sliderConfiguration;
    const {
      sliderCustomStartDate,
      sliderCustomEndDate
    } = this._settings || {};

    return  {
      ...sliderConfiguration,
      sliderCustomStartDate,
      sliderCustomEndDate
    };
  }

  disconnectedCallback () {
    unByKey(this.#eventKeys);
    this.innerHTML = '';
  }

  initChanged () {
    super.initChanged();
    if (this._dateConditionMode === DateConditionMode.SLIDER) {
      this.changed = this.changed || this._sliderCustomStartDateTime || this._sliderCustomEndDateTime;
    }
  }

  reset (triggerConditionChange) {
    this._sliderCustomStartDateTime = undefined;
    this._sliderCustomEndDateTime = undefined;
    if (this.isWorkbookCondition) {
      this.value = this.defaultValue;
      this.changed = false;
      this._handleConditionChangeResponse(this.#conditionApi.setSettingsAndValue({
        sliderCustomStartDate: null,
        sliderCustomEndDate: null
      }, this.conditionValue));
    } else {
      super.reset(triggerConditionChange);
    }
    this._updateButtons();
  }

  get enabled () {
    return super.enabled;
  }

  set enabled (enabled) {
    super.enabled = enabled;
    this.#datePicker.enabled = enabled;
    if (this._dateConditionMode === DateConditionMode.SLIDER) {
      this.#sliderDiv.toggleAttribute('disabled', !enabled);
      this._updateButtons();
    }
  }

  _setStateToConditionsNotAppliedYet () {
    this._setStateToConditionsApplied(false);
  }

  _setStateToConditionsAlreadyApplied () {
    this._setStateToConditionsApplied(true);
  }

  _setStateToConditionsApplied (conditionsNotApplied) {
    this.#conditionsApplied = conditionsNotApplied;
  }

  _updateDynamicConfiguration (conf) {
    this._isInitialized = true;
    if (this._settings) {
      conf.sliderCustomStartDate = this._settings.sliderCustomStartDate;
      conf.sliderCustomEndDate = this._settings.sliderCustomEndDate;
    }
    this._setSliderValues(conf);
    this._initDatePicker(conf);
    this._initSlider();
    this._initSliderLabels();
    this._updateButtons();
  }

  _initSliderLabels () {
    const start = this._sliderCustomStartDateTime || this.#sliderStartDateTime;
    if (start) {
      this._sliderStartLabel.textContent = formatDateWithJavaFormat(start, this.#dateFormat);
    }
    const end = this._sliderCustomEndDateTime || this.#sliderEndDateTime;
    if (end) {
      this._sliderEndLabel.textContent = formatDateWithJavaFormat(end, this.#dateFormat);
    }
  }

  _initAnimationButton (animationAllowed) {
    if (this._canPlayAnimation(animationAllowed)) {
      return Promise.all([
        import(/* webpackChunkName: "animate-action" */'cadenza/conditions/actions/animate-action'),
        // arguments will only be used by classic
        this._timeSliderApiSupplier(this.filterController?.layerId, this.conditionName)
      ]).then(([ { AnimateAction }, timeSliderApi ]) => {
        this.#animateButton = AnimateAction.createAnimateButton(
          this._affectedMapViews,
          timeSliderApi,
          (defaultMaxFrames) => this._createAnimationSettingsDialog(defaultMaxFrames),
          {
            preExecute: this._waitForLayerConditionsSet.bind(this)
          });
        this.#animateButton.classList.add('play-button');
        this._updateAnimateButton();
        this.#nextButton.insertAdjacentElement('afterend', this.#animateButton);
      }).catch(error => {
        logger.error('Failed to import module animate-action and timeslider api', error);
      });
    }
  }

  _createAnimationSettingsDialog (defaultMaxFrames) {
    const { sliderStartDate, sliderEndDate,  sliderCustomEndDate } = this._getSliderValues();
    const pointInTimeMode = !(this._settings?.[CONDITION_SETTINGS.DATE_CONDITION_INTERVAL]
      ?? this._getDateConfiguration().isInterval);
    const smallestUnit = mostFineGrainedTickUnit(this._getDateConfiguration());
    const stepsConfig = pointInTimeMode
      ? { timeWindow: 1, unit: smallestUnit, overlap: 0 }
      : this._stepsConfig;
    return new DateAnimationSettingsDialog(
      this._affectedMapViews,
      stepsConfig,
      this._settings?.[CONDITION_SETTINGS.DATE_CONDITION_FORMAT] || this._getDateConfiguration().dateFormat,
      smallestUnit,
      sliderStartDate,
      this.selectedStartDateTimeValue,
      sliderEndDate,
      sliderCustomEndDate,
      this._getDateConfiguration().sliderConfiguration.sliderAnimationMaxFrames || defaultMaxFrames,
      pointInTimeMode
    );
  }

  _waitForLayerConditionsSet () {
    if (this.#conditionsApplied) {
      return Promise.resolve();
    }
    // The filters must be applied and when the layer conditions are set, the animation
    // can start safely
    return new Promise((resolve) => {
      on(this.filterController, FILTERS_APPLIED, resolve, { once: true });
      this.filterController.notifyApplyFilters();
    });
  }

  _toDateTime (value) {
    return value && toDate(value, { timeZone: this.#timeZoneId });
  }

  // Steps mode: if the conditions or the attributes date format has changed from a fine granularity to a
  // larger granularity - e.g. from days to month - then the start date of the condition has to be
  // adjusted so that it starts at the beginning of that date unit. A granularity of months has to
  // start at the first day of the first month.
  _getAdjustedCustomStartDate (date) {
    if (!this._dateConfiguration) {
      return date;
    }
    const smallestAttributeDateUnit = mostFineGrainedTickUnit(this._dateConfiguration);
    const smallestConditionDateUnit = mostFineGrainedDateFormatTickUnit(this._settings[CONDITION_SETTINGS.DATE_CONDITION_FORMAT]);

    if (this._settings[CONDITION_SETTINGS.DATE_CONDITION_ACTIVE_FILTER_MODE] !== DateConditionFilterMode.STEPS
      || smallestAttributeDateUnit === smallestConditionDateUnit
      || !date) {
      return date;
    }

    // dateUnit is the date granularity in use.
    const dateUnit = smallestConditionDateUnit && TickUnitOrder[smallestAttributeDateUnit] < TickUnitOrder[smallestConditionDateUnit]
      ? smallestConditionDateUnit
      : smallestAttributeDateUnit;
    const isoDate = parseISO(date);
    switch (dateUnit) {
      case TickUnit.YEARS:
        return startOfYear(isoDate);
      case TickUnit.MONTHS:
        return startOfMonth(isoDate);
      case TickUnit.DAYS:
        return startOfDay(isoDate);
      case TickUnit.HOURS:
        return startOfHour(isoDate);
      case TickUnit.MINUTES:
        return startOfMinute(isoDate);
      case TickUnit.SECONDS:
        return startOfSecond(isoDate);
      default:
        return isoDate;
    }
  }

  _setSliderValues (conf) {
    const {
      sliderStartDate,
      sliderEndDate,
      sliderCustomStartDate,
      sliderCustomEndDate,
      sliderLastStartDate,
      sliderLastEndDate
    } = conf;

    const adjustedSliderStartDate = this._getAdjustedCustomStartDate(sliderStartDate);
    const adjustedSliderCustomStartDate = this._getAdjustedCustomStartDate(sliderCustomStartDate);

    if (adjustedSliderStartDate) {
      this.#sliderStartDateTime = this._toDateTime(adjustedSliderStartDate);
    }
    if (sliderEndDate) {
      this.#sliderEndDateTime = this._toDateTime(sliderEndDate);
    }
    if (adjustedSliderCustomStartDate) {
      this._sliderCustomStartDateTime = this._toDateTime(adjustedSliderCustomStartDate);
    }
    if (sliderCustomEndDate) {
      this._sliderCustomEndDateTime = this._toDateTime(sliderCustomEndDate);
    }
    if (sliderLastStartDate) {
      this.#sliderLastStartDateTime = this._toDateTime(sliderLastStartDate);
    }
    if (sliderLastEndDate) {
      this.#sliderLastEndDateTime = this._toDateTime(sliderLastEndDate);
    }
  }

  _getSliderValues () {
    const conf = {};
    if (this.#sliderStartDateTime) {
      conf.sliderStartDate = new Date(this.#sliderStartDateTime);
    }
    if (this.#sliderEndDateTime) {
      conf.sliderEndDate = new Date(this.#sliderEndDateTime);
    }
    if (this._sliderCustomStartDateTime) {
      conf.sliderCustomStartDate = new Date(this._sliderCustomStartDateTime);
    }
    if (this._sliderCustomEndDateTime) {
      conf.sliderCustomEndDate = new Date(this._sliderCustomEndDateTime);
    }
    if (this.#sliderLastStartDateTime) {
      conf.sliderLastStartDate = new Date(this.#sliderLastStartDateTime);
    }
    if (this.#sliderLastEndDateTime) {
      conf.sliderLastEndDate = new Date(this.#sliderLastEndDateTime);
    }
    return conf;
  }

  _initDatePicker (conf) {
    const oldDatePicker = this.#datePicker;
    this._setSliderValues(conf);
    const earliestAndLatest = this._getEarliestAndLatestDate();
    this.#datePicker = new DatePicker({
      title: this.conditionLabel ?? this._title,
      ...this.#staticDatePickerConfiguration,
      ...earliestAndLatest
    });
    if (oldDatePicker) {
      oldDatePicker.remove();
      this.value = getRestrictedDatePickerValues(oldDatePicker, earliestAndLatest.earliestDate, earliestAndLatest.latestDate);
    }
    let elements = [];
    elements.push(this.#prevButton, this.#datePicker, this.#nextButton, this.#animateButton);
    elements = elements.filter(element => !!element);
    this.#datePickerDiv.append(...elements);
    this._toggleStepsButtons();
    this.#datePicker.enabled = this.enabled;
    on(this.#datePicker, 'date-selected', () => this._onDatePickerUpdate());
  }

  // When in steps mode, the steps might not be linear. Use noUiSlider API to get the previous
  // or the next step.
  _updateStepsDelta (direction) {
    const sliderSteps = this.slider.steps()[0];
    if (this._dateConditionMode === DateConditionMode.SLIDER && !this.#isInterval) { // Handle point in time mode
      if (!this.isWorkbookCondition) {
        this._stepDelta = 1;
      } else {
        this._stepDelta = this._calculateStepsDeltaForPointInTime(direction);
      }
    } else if (direction === StepDirection.NEXT) {
      this._stepDelta = sliderSteps[1];
    } else {
      this._stepDelta = sliderSteps[0];
    }
  }

  // For point in time mode, the step size is always one unit of the finest granularity. Since the
  // date condition can have a different date format than the attribute, the steps delta has to
  // be converted to match the attributes date unit.
  _calculateStepsDeltaForPointInTime (direction) {
    const conditionDateUnit = mostFineGrainedDateFormatTickUnit(this._settings[CONDITION_SETTINGS.DATE_CONDITION_FORMAT]);
    // No conditionDateUnit means date format is on auto (same as attribute date format)
    if (!conditionDateUnit || conditionDateUnit === this.#tickUnit) {
      return DEFAULT_STEP_DELTA;
    }
    const start = this.selectedStartDateTimeValue || this.#sliderStartDateTime;
    const duration = {};
    duration[conditionDateUnit] = direction === StepDirection.NEXT ? DEFAULT_STEP_DELTA : -DEFAULT_STEP_DELTA;
    const end = add(start, duration);
    return Math.abs(differenceInTickUnit(start, end, this.#tickUnit));
  }

  _getDateRange () {
    const minTickUnit = mostFineGrainedTickUnit(this._getDateConfiguration());

    let lowerBound = this._sliderCustomStartDateTime || this.#sliderStartDateTime;
    if (lowerBound) {
      lowerBound = roundToTickUnit(lowerBound, minTickUnit);
    }

    let upperBound = this._sliderCustomEndDateTime || this.#sliderEndDateTime;
    if (upperBound) {
      upperBound = roundToTickUnit(upperBound, minTickUnit);
    }

    return lowerBound && upperBound ? { lowerBound, upperBound } : undefined;
  }

  _getEarliestAndLatestDate () {
    const dateRange = this._getDateRange();
    if (dateRange) {
      const { lowerBound: earliestDate, upperBound: latestDate } = dateRange;
      return { earliestDate, latestDate };
    } else {
      return {};
    }
  }

  _initSlider () {
    // Only initialize slider when we have sufficient data
    const dateRange = this._getDateRange();
    if (this.slider) {
      this.slider.off('change');
      this.slider.off('slide');
      unByKey(this._stepsSliderEventKeys);
      this.slider.destroy();
    }
    const duration =
      differenceInTickUnit(dateRange.lowerBound, dateRange.upperBound, this.#tickUnit);
    this.#tickCount = duration + 1;
    if (isNaN(this.#tickCount)) {
      this.#tickCount = 1;
    }
    this.#sliderDiv.classList.toggle('no-ticks', this.#tickCount <= 1);
    if (this.#tickCount <= 1) {
      return;
    }

    const isRelative = this.value.relativeDate !== undefined;
    const relativeDate = this.value.relativeDate;
    const relativeStartAndEndDate = isRelative
      ? this.#datePicker.calcRelativeStartEndDate(relativeDate)
      : null;
    const relativeStartAndEnd = isRelative
      ? [
        Math.min(this._lastTickValue, Math.max(0, this.dateToTickValue(relativeStartAndEndDate[0]))),
        Math.min(this._lastTickValue, Math.max(0, this.dateToTickValue(relativeStartAndEndDate[1])))
      ]
      : [ 0, this._lastTickValue ];
    const handleValues = [ this.selectedStartTickValue || relativeStartAndEnd[0] ];
    if (this.#isInterval) {
      handleValues.push(this.selectedEndTickValue >= 0 ? this.selectedEndTickValue : relativeStartAndEnd[1]);
    }

    let isSteppingMode = this._isSteppingMode;

    // If "Point in time" is selected in the filter configuration, don't use steppingMode.
    if (!this.#isInterval) {
      isSteppingMode = false;
    }
    // This initialization is needed for Cadenza Classic where _initSlider is called before the
    // other initialization of _stepsConfig is executed
    if (!this._stepsConfig) {
      this._stepsConfig = this._createStepsConfig();
    }

    let range;
    if (isSteppingMode) {
      const sliderRangeConfig = this._createSteppedSliderRange();
      range = sliderRangeConfig.sliderRange;
      this._maxLowerTickValue = sliderRangeConfig.lastLowerHandleTickValue;
    } else {
      range = {
        'min': 0,
        'max': this._lastTickValue
      };
    }

    const sliderConfig = {
      animate: !isSteppingMode,
      animationDuration: 100,
      behaviour: 'tap-drag',
      keyboardSupport: true,
      connect: this.#isInterval && !isSteppingMode,
      range,
      start: handleValues
    };

    this.slider = createSlider(this.#sliderDiv, sliderConfig, {
      isUnset: this.isUnset,
      alternativeDesign: true,
      steppingMode: isSteppingMode,
      handleOverlapSliderInteraction: this._handleOverlapSliderInteraction.bind(this)
    });

    if (isSteppingMode) {
      this._setupStepsSliderEventHandlers();
    } else {
      this._setupSliderEventHandlers();
    }

    if (!this.enabled) {
      this.#sliderDiv.setAttribute('disabled', true);
    }
    this.#sliderDiv.append(this.#lastRow);
  }

  // When in steps mode, this method gets passed to the slider as a callback for slide and change
  // events to calculate lower and upper tick values.
  _handleOverlapSliderInteraction (values, type, slider) {
    const tickValues = this._getOverlappingTickValues(values[0]);
    let lowerTickValue = tickValues.lowerTickValue;
    if (type === 'slide' && lowerTickValue > this._maxLowerTickValue) {
      // Set value in slide-handler because it does not invoke slide-handling again
      // see https://refreshless.com/nouislider/events-callbacks/
      slider.set([ this._maxLowerTickValue ]);
      lowerTickValue = this._maxLowerTickValue;
    }

    return { lowerTickValue, upperTickValue: tickValues.upperTickValue };
  }

  _getOverlappingTickValues (value) {
    const maxUpperTickValue = this._lastTickValue;
    const lowerTickValue = parseInt(value);

    const lowerDate = this._tickValueToJSDate(lowerTickValue);
    const upperDate = this._getAdjustedUpperDate(lowerDate);

    let upperTickValue =  this.dateToTickValue(upperDate);
    if (upperTickValue > maxUpperTickValue) {
      upperTickValue = maxUpperTickValue;
    }

    return { lowerTickValue, upperTickValue };
  }

  /**
   * Adjusts the upper end of the date range by subtracting one unit of the smallest available
   * granularity. This applies only when in steps mode.
   *
   * @param {Date} lowerDate - The lower end of the date range.
   * @param {object} [newStepsConfig] - Optionally a newly created steps config. If not set, the current steps config will be used.
   * @return {Date} - The adjusted upper date of the date range.
   * @private
   */
  _getAdjustedUpperDate (lowerDate, newStepsConfig) {
    const { timeWindow, unit } = newStepsConfig || this._stepsConfig;
    const dateUnit = mostFineGrainedTickUnit(this._getDateConfiguration());
    const upperDate = add(lowerDate, { [unit]: timeWindow });
    // The lowest date unit will be "filled up" in the backend, therefore we need to subtract one unit
    // of the smallest available date unit.
    return add(upperDate, { [dateUnit]: -1 });
  }

  get isUnset () {
    return this.value.start === null;
  }

  get selectedStartTickValue () {
    return this.dateTimeToTickValue(this.selectedStartDateTimeValue);
  }

  get selectedStartDateTimeValue () {
    if (!this.value.start) {
      return null;
    }
    return this._toDateTime(this.value.start);
  }

  get selectedEndTickValue () {
    return this.dateTimeToTickValue(this.selectedEndDateTimeValue);
  }

  get selectedEndDateTimeValue () {
    if (!this.value.end) {
      return null;
    }
    return this._toDateTime(this.value.end);
  }

  get _lastTickValue () {
    return this.#tickCount - 1;
  }

  // eslint-disable-next-line accessor-pairs
  set selectedTickValues (tickValues) {
    if (!tickValues.every(Number.isInteger)) {
      return;
    }
    tickValues = tickValues.map(tickValue => Math.max(0, Math.min(tickValue, this._lastTickValue)));

    if (tickValues[0] === this.selectedStartTickValue && (!this.#isInterval || tickValues[1] === this.selectedEndTickValue)) {
      return;
    }
    this.value = {
      start: this._tickValueToJSDate(tickValues[0]),
      end: this.#isInterval ? this._tickValueToJSDate(tickValues[1]) : null
    };
  }

  _onDatePickerUpdate () {
    if (this.#datePicker.values?.start
        && this.#datePicker.values?.end
        && isBefore(parseISO(this.#datePicker.values.end), parseISO(this.#datePicker.values.start))) {
      this.value = { start: this.#datePicker.values.end, end: this.#datePicker.values.start };
    }
    this._updateUi();
    this._adaptStepsConfigAndSaveCondition(true);
  }

  _setupStepsSliderEventHandlers () {
    this._stepsSliderEventKeys.push(on(this.slider.target, 'overlap-slider-change', (event) => {
      const values = event.detail.values;
      this.selectedTickValues = values.map(Math.round);
      this._adaptStepsConfigAndSaveCondition();
    }));
    this._stepsSliderEventKeys.push(on(this.slider.target, 'overlap-slider-slide', (event) => {
      const values = event.detail.values;
      this.selectedTickValues = values.map(Math.round);
    }));
  }

  _setupSliderEventHandlers () {
    this.slider.on('change', values => {
      const newTickValues = values.map(Math.round);
      // if the time slider is interval and there is drag then change is executed 2 times :
      // first time for the start handle and second time for the end handle.
      // with this check we prevent that condition change event is triggered two times
      if (!isEqual(this._lastAppliedTickValues, newTickValues)) {
        this._lastAppliedTickValues = newTickValues;
        this.selectedTickValues = values.map(Math.round);
        this._adaptStepsConfigAndSaveCondition(true);
      }
    });
    this.slider.on('slide', values => {
      this.selectedTickValues = values.map(Math.round);
    });
  }

  _updateSlider () {
    if (this.slider) {
      const start = this.selectedStartTickValue || 0;
      const end = this.selectedEndTickValue >= 0 ? this.selectedEndTickValue : this._lastTickValue;
      const newValue = this.#isInterval ? [ start, end ] : start;
      const currentValue = this.slider.get();
      const changed = this.#isInterval
        ? (start !== Number(currentValue[0]) || end !== Number(currentValue[1]))
        : start !== Number(currentValue);
      if (changed) {
        this.slider.set(newValue);
      }
    }
  }

  get _isSteppingMode () {
    return this.isWorkbookCondition
      ? this._settings[CONDITION_SETTINGS.DATE_CONDITION_ACTIVE_FILTER_MODE] === DateConditionFilterMode.STEPS
      : this._activeFilterMode === DateConditionFilterMode.STEPS;
  }

  // Treatment for relative dates: if the user uses steps buttons, the relative date will be changed
  // into an absolute date. Also the user can step from "outside" the total date range into a valid
  // absolute date. (e.g. total date range ends at 31.12.2005, relative date is set to least year:
  // stepping to a previous date will set 01.01.2004 - 31.12.2005)
  _stepDeltaFromRelativeDate (direction) {
    const relativeStartDate = this.#datePicker.calcRelativeStartEndDate(this.value.relativeDate)[0];
    const relativeStartDateInTickUnits = this.dateTimeToTickValue(relativeStartDate);
    if (direction === StepDirection.PREVIOUS
        && relativeStartDateInTickUnits > this._maxLowerTickValue) {
      return this._maxLowerTickValue;
    } else if (direction === StepDirection.NEXT
        && relativeStartDateInTickUnits < this.slider.options.range.min[0]) {
      return this.slider.options.range.min[0];
    }
    const newStartDate = add(relativeStartDate, {
      [this._stepsConfig.unit]: direction === StepDirection.NEXT
        ? this._stepsConfig.timeWindow
        : -this._stepsConfig.timeWindow
    });
    return this.dateToTickValue(newStartDate);
  }

  _step (direction) {
    let delta;
    let start;
    if (this.value?.relativeDate) {
      delta = this._stepDeltaFromRelativeDate(direction);
      start = this._tickValueToJSDate(0);
    } else {
      this._updateStepsDelta(direction);
      delta = direction === StepDirection.PREVIOUS ? -this._stepDelta : this._stepDelta;
      start = this.selectedStartDateTimeValue || this.#sliderStartDateTime;
    }
    const newStart = this.calculateDatePlusDelta(start, delta);
    let newEnd;
    if (this._isSteppingMode && this.#isInterval) {
      const lowerTickValue = this.dateToTickValue(newStart);
      const upperTickValue = this._getOverlappingTickValues(lowerTickValue).upperTickValue;
      newEnd = this._tickValueToJSDate(upperTickValue);
    } else {
      newEnd = this.calculateDatePlusDelta(newStart, delta);
    }
    this.value = {
      start: newStart,
      end: newEnd
    };
    this._updateUi();
    this._adaptStepsConfigAndSaveCondition();
  }

  _updateButtons () {
    if (this._dateConditionMode === DateConditionMode.SLIDER) {
      const sliderMinValue = Number(this.slider.target.dataset.sliderMinValue);
      this.#prevButton.disabled = this._isPrevButtonDisabled(sliderMinValue);
      this.#nextButton.disabled = this._isNextButtonDisabled(sliderMinValue);
      this._updateAnimateButton();
    }
  }

  _isPrevButtonDisabled (sliderMinValue) {
    const startIsSafeInteger = Number.isSafeInteger(this.selectedStartTickValue);
    if (this.value?.relativeDate) {
      const relativeDateStartInTickUnits = this.dateTimeToTickValue(
        this.#datePicker.calcRelativeStartEndDate(this.value.relativeDate)[0]);
      const relativeDateStepAllowed = canStepFromRelativeDate(
        StepDirection.PREVIOUS, relativeDateStartInTickUnits, sliderMinValue, this._lastTickValue);
      if (this.enabled && relativeDateStepAllowed) {
        return false;
      }
    }
    return !this.enabled
      || !this.selectedStartTickValue
      || (startIsSafeInteger && this.selectedStartTickValue <= sliderMinValue);
  }

  _isNextButtonDisabled (sliderMinValue) {
    const lastTickValue = this.#isInterval ? this.selectedEndTickValue : this.selectedStartTickValue;
    if (this.value?.relativeDate) {
      const relativeDateEndInTickUnits = this.dateTimeToTickValue(
        this.#datePicker.calcRelativeStartEndDate(this.value.relativeDate)[1]);
      const relativeDateStepAllowed = canStepFromRelativeDate(
        StepDirection.NEXT, relativeDateEndInTickUnits, sliderMinValue, this._lastTickValue);
      if (this.enabled && relativeDateStepAllowed) {
        return false;
      }
    }
    return !this.enabled
      || !lastTickValue
      || (Number.isSafeInteger(lastTickValue) && lastTickValue >= this._lastTickValue);
  }

  _updateAnimateButton () {
    if (this.#animateButton) {
      this.#animateButton.disabled = this._shouldDisableAnimateButton();
    }
  }

  _toggleStepsButtons () {
    const showStepsButtons = this._shouldShowStepsButtons();
    const elements = [ this.#prevButton, this.#nextButton ];
    elements.forEach((element) => {
      if (element) {
        element.hidden = !showStepsButtons;
      }
    });
  }

  _shouldShowStepsButtons () {
    let filterMode;
    let sliderMode;
    if (this.isWorkbookCondition) {
      filterMode = this._settings[CONDITION_SETTINGS.DATE_CONDITION_ACTIVE_FILTER_MODE]
        ?? DateConditionFilterMode.DATA_INPUT;
      sliderMode = this._settings[CONDITION_SETTINGS.DATE_CONDITION_MODE]
        ?? DateConditionMode.SLIDER;
    } else {
      filterMode = this._activeFilterMode;
      sliderMode = this._dateConditionMode;
    }
    // Enable steps only if
    // 1: "point-in-time"-mode and slider is visible
    // 2: in steps mode
    return filterMode === DateConditionFilterMode.STEPS
      || !this.#isInterval && sliderMode === DateConditionMode.SLIDER;
  }

  _shouldDisableAnimateButton () {
    if (!this.enabled || !this.slider) {
      return true;
    }
    const isRelative = this.value.relativeDate !== undefined;
    // non-relative non-interval sliders should never be disabled.
    if (!this.#isInterval && !isRelative) {
      return this.selectedStartTickValue === undefined;
    }
    const relativeStartAndEnd = isRelative ? this.#datePicker.calcRelativeStartEndDate(this.value.relativeDate) : null;
    const relativeStartTickValue = isRelative ? this.dateToTickValue(relativeStartAndEnd[0]) : 0;
    if (!this.#isInterval) {
      return !this._isTickValueWithinLimits(relativeStartTickValue);
    }
    if (!isRelative && this._isAnySelectedValueUndefined()) {
      return true;
    }
    // for non-relative dates, it is enough to check for the limit values, so no further checking is needed.
    if (!this.value.relativeDate) {
      return false;
    }
    const relativeStopTickValue = this.dateToTickValue(relativeStartAndEnd[1]);
    return this._relativeTickRangeAllowsForAnimation(relativeStartTickValue, relativeStopTickValue);
  }

  _isTickValueWithinLimits (tickValue) {
    return tickValue < 0 || tickValue >= this.#tickCount - 1;
  }

  _isAnySelectedValueUndefined () {
    return this.selectedStartTickValue === undefined || this.selectedEndTickValue === undefined;
  }

  _relativeTickRangeAllowsForAnimation (relativeStartTickValue, relativeStopTickValue) {
    // animation is only possible if at least some of the range is selected and some unselected.
    return (relativeStartTickValue <= 0 && relativeStopTickValue <= 0)
      || (relativeStartTickValue > this.#tickCount && relativeStopTickValue > this.#tickCount)
      || (relativeStartTickValue <= 0 && relativeStopTickValue >= this.#tickCount - 1);
  }

  get conditionValue () {
    const selectedDates = this.#datePicker.values;
    return datePickerValueToConditionValue(selectedDates);
  }

  _handleConditionChangeResponse (conditionChangeResponsePromise) {
    this._setStateToConditionsNotAppliedYet();
    return conditionChangeResponsePromise
      .then((json) => super.onConditionChange(json))
      .catch(error => this.handleConditionValueChangeError(error));
  }

  onConditionChange () {
    this._adaptStepsConfigAndSaveCondition(true);
  }

  /**
   * Some input methods or condition settings can have an effect on the steps settings.
   * 1. If the user uses date input, then the steps config has to be updated to match that date range.
   * 2. If a relative date is used, then the steps config has to be derived from that condition settings.
   *
   * @param {boolean} dateFromUserInput - Whether the user typed in the date using the date input field.
   * @private
   */
  _adaptStepsConfigAndSaveCondition (dateFromUserInput = false) {
    let stepsConfig = this._stepsConfig;
    if (this.value?.relativeDate) {
      stepsConfig = stepsConfigFromRelativeDate(this.value.relativeDate);
    } else if (dateFromUserInput) {
      stepsConfig = this._adaptStepsConfigToConditionValueIfNecessary(this._stepsConfig, this.conditionValue?.[0]);
    }
    if (this._dateConditionMode === DateConditionMode.SLIDER) {
      this._onSliderConditionChange(stepsConfig);
    } else {
      this._onCalendarConditionChange(stepsConfig);
    }
  }

  _onSliderConditionChange (adaptedStepsConfig) {
    adaptedStepsConfig = adaptedStepsConfig ?? this._stepsConfig;

    if (this.isWorkbookCondition) {
      if (!stepsConfigEquals(adaptedStepsConfig, this._stepsConfig)) {
        this._handleConditionChangeResponse(this.#conditionApi.setSettingsAndValue(
          toConditionSettings(adaptedStepsConfig),
          this.conditionValue));
      } else {
        this._handleConditionChangeResponse(this.#conditionApi.setValue(this.conditionValue));
      }
    } else {
      if (!stepsConfigEquals(adaptedStepsConfig, this._stepsConfig)) {
        this._stepsConfig = adaptedStepsConfig;
      }

      this.filterController.disableOtherConditionsIfNeededForConsistency([ this ]);
      const setValuesPromise = this.#conditionApi.setValue(this.conditionValue, this._getClassicCustomProperties());
      this.#latestSetValuesPromise = setValuesPromise;
      this._handleConditionChangeResponse(setValuesPromise)
        .finally(() => {
        // if the changes are not fully processed yet, disable the other conditions again.
          const enable = setValuesPromise === this.#latestSetValuesPromise;
          this.filterController.setConditionsDisabledForConsistency(enable, this);
        });
    }
  }

  _onCalendarConditionChange (adaptedStepsConfig) {
    const selectedDates = this.value;
    const value = datePickerValueToConditionValue(selectedDates);
    this._updateValueOfResolvesTo();
    if (!stepsConfigEquals(adaptedStepsConfig, this._stepsConfig) && this.isWorkbookCondition) {
      this.#conditionApi.setSettingsAndValue(
        toConditionSettings(adaptedStepsConfig),
        value)
        .then((json) => super.onConditionChange(json))
        .catch(error => this.handleConditionValueChangeError(error));
    } else {
      this.#conditionApi.setValue(value)
        .then((json) => super.onConditionChange(json))
        .catch(error => this.handleConditionValueChangeError(error));
    }
  }

  _updateValueOfResolvesTo () {
    const relativeDate = this.value.relativeDate;
    if (relativeDate) {
      const values = this.#datePicker.calcRelativeStartEndDate(relativeDate);
      this.#resolvedDateValue.textContent = this.#datePicker.formatDateValues({ start: values[0], end: values[1] });
    }
    this.#resolvedDate.hidden = !relativeDate;
  }

  /**
   * Updates workbook on changes of settings and values.
   *
   * @param {object} changedSettings - Settings that have been changed.
   * @param {boolean} conditionChanged - Flag indicating whether the condition values changed.
   */
  _onSettingOrConditionChange (changedSettings, conditionChanged) {
    if (changedSettings && conditionChanged) {
      this._handleConditionChangeResponse(this.#conditionApi.setSettingsAndValue(changedSettings, this.conditionValue));
    } else if (changedSettings) {
      this._handleConditionChangeResponse(this.#conditionApi.setSettings(changedSettings));
    } else if (conditionChanged) {
      this._handleConditionChangeResponse(this.#conditionApi.setValue(this.conditionValue));
    }
  }

  applyChangedSettings (changedSettings) {
    if (isStepsConfigChange(changedSettings) && this.value && this.value.end) {
      const stepsConfig = toStepsConfig(changedSettings);
      const newConditionValue =
        adaptConditionValueToStepsConfig(this.value, stepsConfig);
      if (this._isSteppingMode) {
        newConditionValue.end = this._getAdjustedUpperDate(parseISO(newConditionValue.start), stepsConfig);
      }
      if (!dateConditionRangeValueEquals(this.value, newConditionValue)) {
        this.#conditionApi.setSettingsAndValue(changedSettings, [ [ newConditionValue.start, newConditionValue.end ] ]);
      } else {
        this.#conditionApi.setSettings(changedSettings);
      }
    } else {
      this.#conditionApi.setSettings(changedSettings);
    }
  }

  _tickValueToJSDate (tickValue) {
    if (tickValue < 0) {
      return null;
    }
    const dateRange = this._getDateRange();
    return this.calculateDatePlusDelta(dateRange.lowerBound, tickValue);
  }

  dateTimeToTickValue (date) {
    if (!date) {
      return undefined;
    }
    const dateRange = this._getDateRange();
    return differenceInTickUnit(dateRange.lowerBound, date, this.#tickUnit);
  }

  dateToTickValue (date) {
    if (!date) {
      return undefined;
    }
    return this.dateTimeToTickValue(date);
  }

  get value () {
    return this.#datePicker?.values;
  }

  set value (value) {
    this.#datePicker.values = value;
    this._updateValueOfResolvesTo();
  }

  get persistenceId () {
    return this.getAttribute('persistence-id');
  }

  _updateUi () {
    if (!this._isInitialized) {
      return;
    }
    const isRelative = this.value.relativeDate !== undefined;
    if (isRelative) {
      if (this.#resolvedDate.hidden) {
        this.#sliderDiv.hidden = false;
        this.#lastRow.hidden = false;
        this.#resolvedDate.hidden = false;
      }
      this._updateValueOfResolvesTo();
    } else {
      if (!this.#resolvedDate.hidden) {
        this.#sliderDiv.hidden = false;
        this.#lastRow.hidden = false;
        this.#resolvedDate.hidden = true;
      }
      this._initSlider();
      this._updateSlider();
      this._initSliderLabels();
    }
    this._updateButtons();
    this.#datePickerDiv.classList.toggle('unset', this.isUnset);
    this.initChanged();
  }

  // Classic
  _filterCriteriaChanged (event) {
    const contextSensitiveData = event.detail.eagerlyContextSensitiveData?.[this.persistenceId];
    if (contextSensitiveData) {
      this._updateDynamicConfiguration(contextSensitiveData);
    }
    this._setStateToConditionsNotAppliedYet();
  }

  get numberOfConditionValueRows () {
    const conditionValueArray = datePickerValueToConditionValue(this.value);
    return conditionValueArray ? conditionValueArray.length : 0;
  }

  _onSliderDateRangeChange (event) {
    let conditionChanged = false;
    const changedSettings = event.detail.stepsConfig ? { ...event.detail.stepsConfig } : {};
    const isStart = event.detail.isStart;
    const newRangeDate = event.detail.value;
    // Values in this configuration are of type 'Date'. To make them
    // comparable, convert them.

    if (isStart) {
      const conditionValueStart = this._toDateTime(this.value.start);
      changedSettings.sliderCustomStartDate = newRangeDate;
      this._sliderCustomStartDateTime = newRangeDate;
      if (conditionValueStart && newRangeDate > conditionValueStart) {
        // new slider start date is after current condition value start
        this.#datePicker.values = {
          start: newRangeDate.toISOString(),
          end: this.#datePicker.values.end
        };
        conditionChanged = true;
      }
    } else {
      const conditionValueEnd = this._toDateTime(this.value.start);
      changedSettings.sliderCustomEndDate = newRangeDate;
      this._sliderCustomEndDateTime = newRangeDate;
      if (conditionValueEnd && newRangeDate < conditionValueEnd) {
        // new slider end date is before current condition value end
        this.#datePicker.values = {
          start: this.#datePicker.values.start,
          end: newRangeDate.toISOString()
        };
        conditionChanged = true;
      }
    }
    if (this.isWorkbookCondition) {
      this._onSettingOrConditionChange(changedSettings, conditionChanged);
    } else {
      this.onConditionChange();
    }
  }

  /**
   * Handles change of steps configuration.
   *
   * @param {StepsConfig} stepsConfig - steps settings
   */
  _onStepsConfigChanged (stepsConfig) {
    this._stepsConfig = stepsConfig;
    if (!this.isWorkbookCondition) { // in workbooks the condition is re-initialized anyway
      this._adaptValueToStepsConfig();
    }
  }

  _adaptValueToStepsConfig () {
    const adaptedValue = adaptConditionValueToStepsConfig(this.value, this._stepsConfig);
    if (!dateConditionRangeValueEquals(adaptedValue, this.value)) {
      this.value = adaptedValue;
      this._updateUi();
      this._adaptStepsConfigAndSaveCondition();
    }
  }

  _removeDatePicker (event) {
    unByKey(this.#transientDatePickerEventKey);
    const datePicker = event.target;
    datePicker.remove();
  }

  get contextSensitive () {
    // Both date time condition and date interval condition share this code, date interval condition
    // cannot be context-sensitive and correctly disabled context sensitivity will be in condition settings
    return this._settings?.contextSensitive ?? true;
  }

  // eslint-disable-next-line accessor-pairs
  set contextSensitiveValues (contextSensitiveValues) {
    const minMax = this._minMaxFromContextSensitiveValues(contextSensitiveValues);
    const conf = {
      sliderStartDate: minMax.min,
      sliderEndDate: minMax.max
    };
    if (this._dateConditionMode === DateConditionMode.CALENDAR) {
      // Only update the slider Values for Calendar mode. Slider values is needed if user want to
      // show the slider from date-time-condition-ad-hoc-settings-panel
      this._setSliderValues(conf);
    } else {
      this._updateDynamicConfiguration(conf);
    }
  }

  calculateDatePlusDelta (date, delta) {
    if (!date) {
      return undefined;
    }
    const duration = {};
    duration[this.#tickUnit] = delta;
    return add(date, duration);
  }

  _createDateConditionFilterModeButton (filterModeOptionsCallback) {
    const buttonId = `ad-hoc-settings-${this.dataset.id}`;
    let button;
    // In workbook after setting is applied from adHocSettingsPanel, whole condition re-renders.
    // Due to this we need to check if the flyout is open and reuse the previous button to maintain state
    const existingAdHocSettingsPopup = document.querySelector(`.d-popup[aria-labelledby=${buttonId}]`);
    if (existingAdHocSettingsPopup) {
      const previousButton = existingAdHocSettingsPopup.dPopupTarget;
      const existingPanel = existingAdHocSettingsPopup.querySelector(ADHOC_SETTINGS_PANEL_COMPONENT_NAME);
      // add new listeners on panel as old listeners will be destroyed on this component's disconnectedCallback
      this._listenAdhocSettingsPanelEvent(existingPanel, previousButton);
      button = previousButton;
    } else {
      button = createIconButton(icon(settingsConsole), i18n('dateTimeConditionAdHocSettingsButton.title'), {
        id: buttonId,
        variant: 'link',
        size: 's',
        styleClass: [ 'is-hover-visible' ]
      });
    }
    // remove popup listeners because we can call the setupPopupButton on the same button multiple times
    if (button.removePopupListener) {
      button.removePopupListener();
    }
    button.removePopupListener = setupPopupButton(button, () => {
      const adHocSettingsPanel = new DateTimeConditionAdHocSettingsPanel(filterModeOptionsCallback());
      this._listenAdhocSettingsPanelEvent(adHocSettingsPanel, button);
      return adHocSettingsPanel;
    }, { placement: PLACEMENT.RIGHT_START });
    if (this.isWorkbookCondition) {
      button.style.visibility = this._hasCustomStartAndCustomEndDate() ? '' : 'hidden';
    }
    return button;
  }

  _listenAdhocSettingsPanelEvent (adHocSettingsPanel, button) {
    this.#eventKeys.push(on(
      adHocSettingsPanel,
      SETTINGS_CHANGE,
      event => button.dispatchEvent(new CustomEvent(event.type, { detail: event.detail }))));
    this.#eventKeys.push(on(
      adHocSettingsPanel,
      DATE_RANGE_CHANGE,
      event => this._onSliderDateRangeChange(event)));
  }

  _createSessionStorageKey (setting, conditionId) {
    return `${setting}.${conditionId}`;
  }

  // Creates a range for noUiSlider, that may be non-linear if units like month or year is used.
  _createSteppedSliderRange () {
    const stepsConfig = this._stepsConfig;
    const stepsUnit = stepsConfig.unit;
    const stepSize = stepsConfig.timeWindow - stepsConfig.overlap;
    const dateRange = this._getDateRange();
    const tickUnit = this.#tickUnit;
    const lastTickValue = this._lastTickValue;
    const conditionValue = this.value?.relativeDate
      ? this.#datePicker.calcRelativeStartEndDate(this.value.relativeDate)[0]
      : this.value?.start;
    return calculateSteppedSliderRange(stepSize, tickUnit, lastTickValue, dateRange, stepsUnit, conditionValue, !!this.value?.relativeDate);
  }

  // Workbook only
  _hasCustomStartAndCustomEndDate () {
    return this._settings?.sliderCustomStartDate && this._settings?.sliderCustomEndDate;
  }

  // When custom start and end dates are set, then the condition is not context sensitive anymore.
  // Still the min and max values for the date range are needed in case the user resets the
  // custom dates in the ad-hoc-settings-panel.
  async _getContextSensitiveValues () {
    const data = await getWorkbookConditionContextSensitiveData(getWorkbookIdAndHash(store.getState()), this.dataset.id);
    const minMax = this._minMaxFromContextSensitiveValues(data);
    return { start: new Date(minMax.min), end: new Date(minMax.max) };
  }

  _minMaxFromContextSensitiveValues (contextSensitiveValues) {
    let min = null;
    let max = null;
    contextSensitiveValues?.rows.forEach(({ values }) => {
      const [ sliderStartDate, sliderEndDate ] = values;
      if (!min || this._toDateTime(sliderStartDate) < this._toDateTime(min)) {
        min = sliderStartDate;
      }
      if (!max || this._toDateTime(sliderEndDate) > this._toDateTime(max)) {
        max = sliderEndDate;
      }
    });

    return { min, max };
  }

}

function datePickerValueToConditionValue (value) {
  if (value.relativeDate) {
    return [ [ value.relativeDate ] ];
  }
  if (value.start || value.end) {
    return [ [ value.start, value.end ] ];
  }
  return null;
}

function restrictIsoDate (isoDate, earliestIso, latestIso) {
  if (earliestIso > isoDate) {
    return earliestIso;
  } else if (latestIso < isoDate) {
    return latestIso;
  } else {
    return isoDate;
  }
}

function getRestrictedDatePickerValues (datePicker, earliestDate, latestDate) {
  // cannot restrict a relative date without breaking the date condition
  const earliestIso = earliestDate && !isNaN(earliestDate.getDate())
    ? earliestDate.toISOString()
    : datePicker.values.start;
  const latestIso = latestDate && !isNaN(latestDate.getDate())
    ? latestDate.toISOString()
    : datePicker.values.end;
  const relativeDate = datePicker.values.relativeDate;
  if (datePicker.values.relativeDate) {
    return {
      // cannot restrict a relativeDate
      relativeDate,
      start: restrictIsoDate(datePicker.values.start, earliestIso, latestIso),
      end: restrictIsoDate(datePicker.values.end, earliestIso, latestIso)
    };
  } else if (datePicker.values.end) {
    return {
      start: restrictIsoDate(datePicker.values.start, earliestIso, latestIso),
      end: restrictIsoDate(datePicker.values.end, earliestIso, latestIso)
    };
  } else {
    return {
      start: restrictIsoDate(datePicker.values.start, earliestIso, latestIso)
    };
  }
}

function tickUnitToJavaChronoUnit (tickUnit) {
  return capitalizeFirstLetter(tickUnit);
}

function capitalizeFirstLetter (string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

function isStepsConfigChange (changedSettings) {
  return !!changedSettings[CONDITION_SETTINGS.DATE_CONDITION_INTERVAL_LENGTH];
}

customElements.define(COMPONENT_NAME, DateTimeCondition);
